import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { format } from 'date-fns'
import DeleteEventModal from '../../components/DeleteEventModal'

export default function EventDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  
  // Main states
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [statsSubTab, setStatsSubTab] = useState('confirmations')
  
  // Stats
  const [stats, setStats] = useState({
    confirmationRate: 0,
    attendanceRate: 0,
    absenceRate: 0,
    confirmed: 0,
    attended: 0
  })
  
  // Data arrays
  const [confirmations, setConfirmations] = useState([])
  const [attendance, setAttendance] = useState([])
  const [absence, setAbsence] = useState([])
  
  // Filters
  const [teams, setTeams] = useState([])
  const [selectedTeam, setSelectedTeam] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 25
  
  // Loading
  const [loadingData, setLoadingData] = useState(false)
  
  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)

  useEffect(() => {
    loadEvent()
    loadTeams()
  }, [id])

  useEffect(() => {
    if (activeTab === 'stats') {
      loadStatsData()
      // Auto-switch from absence tab if event is not past
      if (event && statsSubTab === 'absence' && !isEventPast()) {
        setStatsSubTab('confirmations')
      }
    }
  }, [activeTab, statsSubTab, selectedTeam, currentPage, event])

  const loadEvent = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      setEvent(data)
    } catch (error) {
      console.error('Load event error:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTeams = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data } = await supabase
        .from('groups')
        .select('id, name')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)
        .order('name')

      setTeams(data || [])
    } catch (error) {
      console.error('Load teams error:', error)
    }
  }

  const loadStatsData = async () => {
    setLoadingData(true)
    try {
      // Get total number of people in the organization
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { count: totalMembers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', userData.org_id)

      // Load confirmations (only confirmed status)
      const { data: confData } = await supabase
        .from('event_confirmations')
        .select('id, event_id, user_id, status, confirmed_at, user:user_id(id, name, email, profile_photo_url, avatar_url, phone)')
        .eq('event_id', id)
        .eq('status', 'confirmed')
        .order('confirmed_at', { ascending: false })

      // Load attendance
      const { data: attData } = await supabase
        .from('attendance_records')
        .select('id, event_id, user_id, marked_at, user:user_id(id, name, email, profile_photo_url, avatar_url, phone)')
        .eq('event_id', id)
        .order('marked_at', { ascending: false })

      // Enrich with team data - include full hierarchy
      const allUserIds = [...new Set([
        ...(confData?.map(c => c.user_id) || []),
        ...(attData?.map(a => a.user_id) || [])
      ])]

      // Get all group memberships with path
      const { data: allMemberships } = await supabase
        .from('group_members')
        .select('user_id, groups!inner(id, name, path)')
        .in('user_id', allUserIds)

      // Extract all unique team IDs from paths (first segment = team)
      const teamIds = new Set()
      allMemberships?.forEach(m => {
        const teamId = m.groups.path.split('.')[0]
        teamIds.add(teamId)
      })

      // Get all team names in one query
      const { data: teams } = await supabase
        .from('groups')
        .select('id, name')
        .in('id', Array.from(teamIds))

      // Create team lookup
      const teamLookup = {}
      teams?.forEach(t => {
        teamLookup[t.id] = t
      })

      // Map users to their teams (depth=1 ancestor)
      const teamMap = {}
      allMemberships?.forEach(m => {
        const teamId = m.groups.path.split('.')[0]
        teamMap[m.user_id] = teamLookup[teamId]
      })

      const enrichedConfirmations = confData?.map(c => ({
        ...c,
        team: teamMap[c.user_id] || null
      })) || []

      const enrichedAttendance = attData?.map(a => ({
        ...a,
        team: teamMap[a.user_id] || null
      })) || []

      setConfirmations(enrichedConfirmations)
      setAttendance(enrichedAttendance)

      // Calculate absence - ALL members who didn't attend (not just confirmed)
      const attendedUserIds = enrichedAttendance.map(a => a.user_id)
      
      // Get ALL organization members
      const { data: allMembers } = await supabase
        .from('users')
        .select('id, name, email, profile_photo_url, avatar_url, phone')
        .eq('org_id', userData.org_id)
      
      // Filter out those who attended
      const absentMembers = allMembers?.filter(m => !attendedUserIds.includes(m.id)) || []
      
      // Get team data for absent members
      const absentUserIds = absentMembers.map(m => m.id)
      const { data: absentMemberships } = await supabase
        .from('group_members')
        .select('user_id, groups!inner(id, name, path)')
        .in('user_id', absentUserIds)
      
      // Extract team IDs from paths for absent members
      const absentTeamIds = new Set()
      absentMemberships?.forEach(m => {
        const teamId = m.groups.path.split('.')[0]
        absentTeamIds.add(teamId)
      })
      
      // Get team names for absent members
      const { data: absentTeams } = await supabase
        .from('groups')
        .select('id, name')
        .in('id', Array.from(absentTeamIds))
      
      const absentTeamLookup = {}
      absentTeams?.forEach(t => {
        absentTeamLookup[t.id] = t
      })
      
      // Map absent users to their teams
      const absentTeamMap = {}
      absentMemberships?.forEach(m => {
        const teamId = m.groups.path.split('.')[0]
        absentTeamMap[m.user_id] = absentTeamLookup[teamId]
      })
      
      // Get confirmation data for absent members
      const { data: absentConfirmations } = await supabase
        .from('event_confirmations')
        .select('user_id, confirmed_at')
        .eq('event_id', id)
        .in('user_id', absentUserIds)
        .eq('status', 'confirmed')
      
      const confirmationMap = {}
      absentConfirmations?.forEach(c => {
        confirmationMap[c.user_id] = c.confirmed_at
      })
      
      // Enrich absent members with user data, team, and confirmation timestamp
      const enrichedAbsence = absentMembers.map(member => ({
        id: `absence-${member.id}`,
        event_id: id,
        user_id: member.id,
        user: member,
        team: absentTeamMap[member.id] || null,
        confirmed_at: confirmationMap[member.id] || null
      }))
      
      setAbsence(enrichedAbsence)

      // Calculate stats based on total members
      const totalConfirmed = enrichedConfirmations.length
      const totalAttended = enrichedAttendance.length
      const totalAbsent = enrichedAbsence.length

      const confirmationRate = totalMembers > 0 ? Math.round((totalConfirmed / totalMembers) * 100) : 0
      const attendanceRate = totalMembers > 0 ? Math.round((totalAttended / totalMembers) * 100) : 0
      const absenceRate = totalMembers > 0 ? Math.round((totalAbsent / totalMembers) * 100) : 0

      setStats({
        confirmationRate,
        attendanceRate,
        absenceRate,
        confirmed: totalConfirmed,
        attended: totalAttended
      })

    } catch (error) {
      console.error('Load stats error:', error)
    } finally {
      setLoadingData(false)
    }
  }

  const handleDeleteSuccess = () => {
    navigate('/admin/events')
  }

  const isEventPast = () => {
    if (!event) return false
    const eventEndDateTime = new Date(`${event.start_date}T${event.end_time}`)
    return eventEndDateTime < new Date()
  }

  const exportToCSV = () => {
    try {
      let data = []
      let filename = ''
      let currentData = []

      // Filter by team if selected
      if (statsSubTab === 'confirmations') {
        currentData = confirmations
      } else if (statsSubTab === 'attendance') {
        currentData = attendance
      } else {
        currentData = absence
      }

      // Apply team filter
      if (selectedTeam !== 'all') {
        currentData = currentData.filter(item => item.team?.id === selectedTeam)
      }

      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        currentData = currentData.filter(item =>
          item.user?.name?.toLowerCase().includes(search) ||
          item.user?.email?.toLowerCase().includes(search)
        )
      }

      if (statsSubTab === 'confirmations') {
        data = [
          ['Name', 'Email', 'Phone Number', 'Status', 'Date'],
          ...currentData.map(c => [
            c.user?.name || '',
            c.user?.email || '',
            c.user?.phone || '-',
            '✓ Confirmed',
            format(new Date(c.confirmed_at), 'MMM dd, yyyy h:mm a')
          ])
        ]
        filename = `${event.title}-confirmations-${format(new Date(), 'yyyy-MM-dd')}.csv`
      } else if (statsSubTab === 'attendance') {
        data = [
          ['Name', 'Email', 'Phone Number', 'Marked At'],
          ...currentData.map(a => [
            a.user?.name || '',
            a.user?.email || '',
            a.user?.phone || '-',
            format(new Date(a.marked_at), 'MMM dd, yyyy h:mm a')
          ])
        ]
        filename = `${event.title}-attendance-${format(new Date(), 'yyyy-MM-dd')}.csv`
      } else {
        data = [
          ['Name', 'Email', 'Phone Number', 'Confirmed At'],
          ...currentData.map(a => [
            a.user?.name || '',
            a.user?.email || '',
            a.user?.phone || '-',
            a.confirmed_at ? format(new Date(a.confirmed_at), 'MMM dd, yyyy h:mm a') : '-'
          ])
        ]
        filename = `${event.title}-absence-${format(new Date(), 'yyyy-MM-dd')}.csv`
      }

      const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export error:', error)
      alert('Failed to export: ' + error.message)
    }
  }

  // Get filtered data for current tab
  const getFilteredData = () => {
    let data = []
    if (statsSubTab === 'confirmations') data = confirmations
    else if (statsSubTab === 'attendance') data = attendance
    else data = absence

    // Apply team filter
    if (selectedTeam !== 'all') {
      data = data.filter(item => item.team?.id === selectedTeam)
    }

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      data = data.filter(item =>
        item.user?.name?.toLowerCase().includes(search) ||
        item.user?.email?.toLowerCase().includes(search)
      )
    }

    return data
  }

  const filteredData = getFilteredData()
  const totalCount = filteredData.length
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)
  const paginatedData = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf9fb] flex items-center justify-center">
        <div>Loading...</div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#faf9fb] flex items-center justify-center">
        <div>
          <p>Event not found</p>
          <Link to="/admin/events">Back to Events</Link>
        </div>
      </div>
    )
  }

  const getTypeBadge = (type) => {
    if (type === 'physical') return 'Physical'
    if (type === 'virtual') return 'Virtual'
    return 'Hybrid'
  }

  const getStatusBadge = (status) => {
    if (status === 'scheduled') return 'Upcoming'
    if (status === 'ongoing') return 'Active'
    return 'Past'
  }

  return (
    <div className="min-h-screen bg-[#faf9fb] py-8 px-4">
      {/* Back Button */}
      <div className="max-w-6xl mx-auto mb-6">
        <Link to="/admin/events" className="text-[#1e293b] hover:text-[#1e293b] font-semibold">
          ← Back to events
        </Link>
      </div>

      {/* Main White Card */}
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Main Tabs */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex gap-3">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-2 font-medium rounded-full transition-colors ${
                activeTab === 'overview'
                  ? 'bg-[#eaf0ff] text-[#3167fe] border border-[#3167fe]'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Event overview
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-6 py-2 font-medium rounded-full transition-colors ${
                activeTab === 'stats'
                  ? 'bg-[#eaf0ff] text-[#3167fe] border border-[#3167fe]'
                  : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Event stats
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-8 pb-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Banner */}
              <div className="flex">
                {event.banner_url ? (
                  <img
                    src={event.banner_url}
                    alt={event.title}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full h-full bg-blue-50 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" className="text-blue-300">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Right Column - Details */}
              <div className="space-y-4">
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                    {getTypeBadge(event.event_type)}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full">
                    {getStatusBadge(event.event_status)}
                  </span>
                </div>

                <h2 className="text-2xl font-bold text-gray-900">{event.title}</h2>
                
                {event.description && (
                  <p className="text-gray-600">{event.description}</p>
                )}

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-1">Date</p>
                    <p className="text-lg text-gray-900">{format(new Date(event.start_date), 'MMMM dd, yyyy')}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-semibold text-gray-500 mb-1">Time</p>
                    <p className="text-lg text-gray-900">{event.start_time} - {event.end_time}</p>
                  </div>

                  {(event.event_type === 'physical' || event.event_type === 'hybrid') && event.venue && (
                    <div>
                      <p className="text-sm font-semibold text-gray-500 mb-1">Venue</p>
                      <p className="text-lg text-gray-900">{event.venue}</p>
                    </div>
                  )}

                  {(event.event_type === 'virtual' || event.event_type === 'hybrid') && event.meeting_link && (
                    <div>
                      <p className="text-sm font-semibold text-gray-500 mb-1">Link</p>
                      <a 
                        href={event.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-lg text-blue-600 hover:text-blue-700 underline"
                      >
                        {event.meeting_link}
                      </a>
                    </div>
                  )}
                </div>

                {!isEventPast() && (
                  <div className="flex gap-3 pt-4">
                    <Link
                      to={`/admin/events/${event.id}/edit`}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Edit event
                    </Link>
                    <button
                      onClick={() => setDeleteModalOpen(true)}
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Delete event
                    </button>
                  </div>
                )}

                {isEventPast() && (
                  <p className="text-sm text-gray-500 italic pt-4">
                    This event has ended. Edit and delete options are no longer available.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'stats' && (
            <div className="space-y-8">
              {/* Summary Statistics */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Summary</h3>
                <div className={`grid grid-cols-1 gap-4 ${isEventPast() ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                  <div className="bg-[#f1f5f9] rounded-lg p-6">
                    <p className="text-5xl font-bold text-gray-900 mb-2">{stats.confirmationRate}%</p>
                    <p className="text-gray-600">Confirmation rate</p>
                  </div>
                  <div className="bg-[#f1f5f9] rounded-lg p-6">
                    <p className="text-5xl font-bold text-gray-900 mb-2">{stats.attendanceRate}%</p>
                    <p className="text-gray-600">Attendance rate</p>
                  </div>
                  {isEventPast() && (
                    <div className="bg-[#f1f5f9] rounded-lg p-6">
                      <p className="text-5xl font-bold text-gray-900 mb-2">{stats.absenceRate}%</p>
                      <p className="text-gray-600">Absence rate</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sub-tabs */}
              <div>
                <div className="border-b mb-6">
                  <div className="flex">
                    <button
                      onClick={() => {
                        setStatsSubTab('confirmations')
                        setCurrentPage(1)
                      }}
                      className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                        statsSubTab === 'confirmations'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Confirmed ({confirmations.length})
                    </button>
                    <button
                      onClick={() => {
                        setStatsSubTab('attendance')
                        setCurrentPage(1)
                      }}
                      className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                        statsSubTab === 'attendance'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Attended ({attendance.length})
                    </button>
                    {isEventPast() && (
                      <button
                        onClick={() => {
                          setStatsSubTab('absence')
                          setCurrentPage(1)
                        }}
                        className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                          statsSubTab === 'absence'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        Declined ({absence.length})
                      </button>
                    )}
                  </div>
                </div>

                {/* Search, Filter, Export and Table - All in one bordered container */}
                <div className="border border-gray-200 rounded-lg p-6">
                  {/* Search, Filter and Export */}
                  <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[200px]">
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setCurrentPage(1)
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={selectedTeam}
                    onChange={(e) => {
                      setSelectedTeam(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Teams</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  <button
                    onClick={exportToCSV}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Export CSV
                  </button>
                </div>

                  {/* Table */}
                  {loadingData ? (
                    <div className="text-center py-12 text-gray-500">Loading...</div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Member</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Email</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone Number</th>
                            {statsSubTab === 'confirmations' && (
                              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                            )}
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">
                              {statsSubTab === 'confirmations' && 'Date'}
                              {statsSubTab === 'attendance' && 'Marked At'}
                              {statsSubTab === 'absence' && 'Confirmed At'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.map((item) => (
                            <tr key={item.id || item.user_id} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  {(item.user?.profile_photo_url || item.user?.avatar_url) ? (
                                    <img
                                      src={item.user.profile_photo_url || item.user.avatar_url}
                                      alt={item.user?.name}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                      <span className="text-blue-700 text-sm font-semibold">
                                        {item.user?.name?.charAt(0) || '?'}
                                      </span>
                                    </div>
                                  )}
                                  <span className="font-medium text-gray-900">{item.user?.name || 'Unknown'}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">{item.user?.email || '-'}</td>
                              <td className="px-4 py-3 text-gray-600">{item.user?.phone || '-'}</td>
                              {statsSubTab === 'confirmations' && (
                                <td className="px-4 py-3">
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                    ✓ Confirmed
                                  </span>
                                </td>
                              )}
                              <td className="px-4 py-3 text-gray-600 text-xs">
                                {statsSubTab === 'confirmations' && (item.confirmed_at ? format(new Date(item.confirmed_at), 'MMM dd, yyyy h:mm a') : '-')}
                                {statsSubTab === 'attendance' && (item.marked_at ? format(new Date(item.marked_at), 'MMM dd, yyyy h:mm a') : '-')}
                                {statsSubTab === 'absence' && (item.confirmed_at ? format(new Date(item.confirmed_at), 'MMM dd, yyyy h:mm a') : '-')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Empty State */}
                    {paginatedData.length === 0 && (
                      <div className="text-center py-12 text-gray-500">
                        {searchTerm ? 'No results match your search' : 'No data available'}
                      </div>
                    )}

                    {/* Pagination */}
                    {totalCount > PAGE_SIZE && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <p className="text-sm text-gray-600">
                          Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} results
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            First
                          </button>
                          <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 border rounded bg-blue-50 text-blue-600 font-medium">
                            {currentPage}
                          </span>
                          <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                          <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DeleteEventModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        event={event}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  )
}